/**
 * ChallengesList Component
 * Displays challenges in sections with filter bar. Uses FlashList v2 (no estimatedItemSize).
 */

import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { FlashList, FlashListRef, ListRenderItemInfo } from '@shopify/flash-list';
import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../src/shared/constants/flashList.constants';
import { router, useFocusEffect } from 'expo-router';
import { ChevronRight, ListFilter } from 'lucide-react-native';
import { ChallengeCard } from './ChallengeCard';
import { ChallengeCardSkeleton } from './ChallengeCardSkeleton';
import { useFirstChallengeTarget } from '../../src/onboarding/hooks/useFirstChallengeTarget';
import { FC_TARGET_KEYS } from '../../src/onboarding/constants/firstChallengeTour.constants';
import { ArenaLoadingSkeleton } from './ArenaLoadingSkeleton/ArenaLoadingSkeleton';
import { useChallengesPage } from '../../src/challenges/contexts/ChallengesPageContext';
import { getChallengesPageStyles } from '../../src/challenges/styles/challenges.styles';
import { useSkeletonLoading } from '../../src/shared/hooks/useSkeletonLoading';
import { FadeInContent } from '../shared/FadeInContent';
import { StaggeredFadeIn } from '../shared/StaggeredFadeIn';
import { GuestBanner } from '../../src/shared/components/GuestBanner/GuestBanner';
import { useTheme } from '../../src/theme/useTheme';
import { FILTER_OPTIONS } from '../../src/challenges/types/challenges.types';
import {
  ARENA_EMPTY_STATE_COPY,
  ARENA_LIST_HEADER_OFFSET,
  EMPTY_FILTER_SUBTITLE,
  SECTION_TITLES,
} from '../../src/challenges/constants/challenges.constants';
import {
  buildArenaFlatList,
  findArenaListRowIndexForChallengeId,
} from '../../src/challenges/helpers/arenaFlatListBuilder.helper';
import type { ArenaListItem } from '../../src/challenges/types/challenges.types';
import { useGuestGuard } from '../../src/shared/hooks/useGuestGuard';
import { ArenaStatsBanner } from './ArenaStatsBanner';
import { ArenaSectionHeader } from './ArenaSectionHeader/ArenaSectionHeader';
import { GameResultModal } from './GameResultModal/GameResultModal';
import { useUnseenChallengeResults } from '../../src/challenges/hooks/useUnseenChallengeResults';
import { ArenaEmptyStateMonkey } from './ArenaEmptyStateMonkey/ArenaEmptyStateMonkey';
import { useUserProfile } from '../../src/shared/contexts/UserProfileContext';
import { DataLoadErrorPanel } from '../../src/shared/components/DataLoadErrorPanel/DataLoadErrorPanel';
import { ARENA_CHALLENGES_ERROR_COPY } from '../../src/shared/constants/dataLoadError.constants';

const SECTION_TITLES_FINISHED = SECTION_TITLES.finished;

// Module-level cooldown map: survives tab-switch remounts, scoped by user, resets after 2 min
const lastModalShownByUser = new Map<string, number>();
const MODAL_COOLDOWN_MS = 2 * 60 * 1000;

export const ChallengesList: React.FC = () => {
  const theme = useTheme();
  const { isGuest, requireAuth } = useGuestGuard();
  const challengesPageStyles = useMemo(
    () => getChallengesPageStyles(theme),
    [theme]
  );

  const {
    isLoading,
    isActiveError,
    isLoadingHistory,
    isLoadingHistoryPreview,
    isInitialLoadComplete,
    challenges,
    filteredChallenges,
    historyChallenges,
    historyPreview,
    hasNextPage,
    fetchMoreHistory,
    isFetchingNextPage,
    activeFilter,
    setActiveFilter,
    sectionBuckets,
    currentUserId,
    handleChallengePress,
    handleShowMessage,
    scrollTargetChallengeId,
    setExpandedChallengeId,
    clearExpandChallengeId,
    refetchActive,
  } = useChallengesPage();

  const listRef = useRef<FlashListRef<ArenaListItem>>(null);
  const hasAppliedExpandForTargetRef = useRef<string | null>(null);
  /** After pivoting to Finished for expandChallenge, skip forcing All again. */
  const deepLinkFinishedTabRef = useRef(false);
  /** Cancels in-flight scroll retries when the expandChallenge effect cleans up. */
  const expandChallengeScrollCancelledRef = useRef(false);

  // ── Profile (arena stats + game result modal) ──────────────────────────────
  const {
    profile,
    isLoading: userProfileLoading,
    error: userProfileError,
    refetchProfile,
  } = useUserProfile();

  useFocusEffect(
    useCallback(() => {
      refetchProfile();
    }, [refetchProfile])
  );
  const currentUser = useMemo(
    () => ({
      id: currentUserId,
      lastSeenResultsAt: profile?.lastSeenResultsAt ?? null,
      avatarId: profile?.avatarId,
      avatarConfig: profile?.avatarConfig ?? null,
    }),
    [currentUserId, profile?.lastSeenResultsAt, profile?.avatarId, profile?.avatarConfig],
  );
  const { pendingResults, hasOverflow, markAllSeen } = useUnseenChallengeResults(
    historyPreview,
    currentUser,
  );
  const [resultModalVisible, setResultModalVisible] = useState(false);

  // Primitive dep to avoid re-render loop from array reference instability
  const pendingIds = pendingResults.map((r) => r.challengeId ?? '').join(',');
  useEffect(() => {
    const now = Date.now();
    const lastShownAt = lastModalShownByUser.get(currentUserId) ?? 0;
    if (pendingResults.length > 0 && now - lastShownAt > MODAL_COOLDOWN_MS) {
      lastModalShownByUser.set(currentUserId, now);
      setResultModalVisible(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingIds, currentUserId]);
  const hasUserScrolledRef = useRef(false);
  const hasScrolledToTopOnceRef = useRef(false);
  /** Once we've left full-page skeleton, never show it again (e.g. when tapping Finished and isInitialLoadComplete flips to false). */
  const hasRevealedOnceRef = useRef(false);

  const challengesList = challenges ?? [];
  const historyList = useMemo(
    () => (Array.isArray(historyChallenges) ? historyChallenges : []),
    [historyChallenges]
  );
  const previewList = useMemo(
    () => (Array.isArray(historyPreview) ? historyPreview : []),
    [historyPreview]
  );
  const filteredList = filteredChallenges ?? [];
  const arenaStats = profile?.arenaStats ?? null;
  const arenaStatsLoading = userProfileLoading;
  const arenaStatsError = userProfileError;

  const isContentReadyBase =
    !arenaStatsLoading &&
    !isLoadingHistoryPreview &&
    (challengesList.length > 0 ||
      historyList.length > 0 ||
      isInitialLoadComplete);
  if (isContentReadyBase) hasRevealedOnceRef.current = true;
  const isContentReady = hasRevealedOnceRef.current || isContentReadyBase;

  const { isLoadingWithDelay, contentOpacity } = useSkeletonLoading({
    isLoading: isLoading && !hasRevealedOnceRef.current,
    isContentReady,
  });

  const isEmptyState =
    isInitialLoadComplete &&
    !isLoadingWithDelay &&
    !isLoadingHistoryPreview &&
    challengesList.length === 0 &&
    previewList.length === 0 &&
    historyList.length === 0;
  const isFilteredEmpty =
    isInitialLoadComplete &&
    !isLoadingWithDelay &&
    (challengesList.length > 0 || historyList.length > 0) &&
    filteredList.length === 0;

  const listData = useMemo(
    () =>
      buildArenaFlatList({
        sectionBuckets,
        historyChallenges: historyList,
        historyPreview: previewList,
        isLoadingHistoryPreview: isLoadingHistoryPreview && previewList.length === 0,
        hasNextPage: hasNextPage ?? false,
        activeFilter,
        isLoadingHistory: isLoadingHistory && historyList.length === 0,
        isInitialLoadComplete,
      }),
    [sectionBuckets, historyList, previewList, isLoadingHistoryPreview, hasNextPage, activeFilter, isLoadingHistory, isInitialLoadComplete]
  );

  // Reset "already applied" when no scroll target so a new deep link can run again
  useEffect(() => {
    if (!scrollTargetChallengeId) {
      hasAppliedExpandForTargetRef.current = null;
      deepLinkFinishedTabRef.current = false;
    }
  }, [scrollTargetChallengeId]);

  // Keep list at top when first loading completes (no deep link). Prevents ending up at bottom on Arena load.
  useEffect(() => {
    if (!isInitialLoadComplete || scrollTargetChallengeId || hasScrolledToTopOnceRef.current) return;
    if (listData.length === 0) return;
    hasScrolledToTopOnceRef.current = true;
    const t = setTimeout(() => {
      try {
        (listRef.current as unknown as { scrollToOffset?: (opts: { offset: number; animated?: boolean }) => void })?.scrollToOffset?.({ offset: 0, animated: false });
      } catch {
        // ignore
      }
    }, 50);
    return () => clearTimeout(t);
  }, [isInitialLoadComplete, scrollTargetChallengeId, listData.length]);

  // expandChallenge: default All (preview strip); if id not on list after preview loads, open Finished tab once.
  useEffect(() => {
    if (!scrollTargetChallengeId || listData.length === 0) return;
    if (hasAppliedExpandForTargetRef.current === scrollTargetChallengeId) return;
    if (!deepLinkFinishedTabRef.current) setActiveFilter('active');
    if (!isInitialLoadComplete || isLoadingHistoryPreview) return;
    const row = findArenaListRowIndexForChallengeId(listData, scrollTargetChallengeId);
    if (row >= 0 || activeFilter !== 'active') return;
    deepLinkFinishedTabRef.current = true;
    setActiveFilter('finished');
  }, [
    scrollTargetChallengeId,
    listData,
    isInitialLoadComplete,
    isLoadingHistoryPreview,
    activeFilter,
    setActiveFilter,
  ]);

  useEffect(() => {
    if (!scrollTargetChallengeId || listData.length === 0) return;
    if (hasAppliedExpandForTargetRef.current === scrollTargetChallengeId) return;
    const rowIndex = findArenaListRowIndexForChallengeId(listData, scrollTargetChallengeId);
    if (rowIndex < 0) return;

    const targetId = scrollTargetChallengeId;
    expandChallengeScrollCancelledRef.current = false;

    // Expand first, then scroll. Do NOT clearExpandChallengeId until after scroll — clearing the URL
    // drops scrollTargetChallengeId and this effect's cleanup cancels the scroll timer before it runs.
    const expandDelayMs = 50;
    const scrollAfterExpandMs = 380;
    const scrollMaxAttempts = 12;
    const scrollRetryMs = 100;

    const expandTimer = setTimeout(() => {
      if (expandChallengeScrollCancelledRef.current) return;
      setExpandedChallengeId(targetId);
    }, expandDelayMs);

    const scrollTimer = setTimeout(() => {
      const tryScroll = (attempt: number) => {
        if (expandChallengeScrollCancelledRef.current) return;
        try {
          listRef.current?.scrollToIndex({
            index: rowIndex,
            animated: true,
            viewPosition: 0,
            viewOffset: -ARENA_LIST_HEADER_OFFSET,
          });
        } catch {
          if (attempt + 1 < scrollMaxAttempts) {
            setTimeout(() => tryScroll(attempt + 1), scrollRetryMs);
            return;
          }
        }
        if (expandChallengeScrollCancelledRef.current) return;
        hasAppliedExpandForTargetRef.current = targetId;
        clearExpandChallengeId();
      };
      tryScroll(0);
    }, scrollAfterExpandMs);

    return () => {
      expandChallengeScrollCancelledRef.current = true;
      clearTimeout(expandTimer);
      clearTimeout(scrollTimer);
    };
  }, [
    scrollTargetChallengeId,
    listData,
    setExpandedChallengeId,
    clearExpandChallengeId,
  ]);

  const handleEndReached = useCallback(() => {
    if (!hasUserScrolledRef.current) return;
    if (hasNextPage && !isFetchingNextPage) fetchMoreHistory();
  }, [hasNextPage, isFetchingNextPage, fetchMoreHistory]);

  const handleScroll = useCallback(() => {
    hasUserScrolledRef.current = true;
  }, []);

  // First-challenge tour: spotlight the first (active) section — where the just-sent challenge lands.
  const { viewRef: liveCardRef } = useFirstChallengeTarget(FC_TARGET_KEYS.live);

  const renderItem = useCallback(
    ({ item, index: listIndex }: ListRenderItemInfo<ArenaListItem>) => {
      if (item.type === 'historyPreview') {
        return (
          <StaggeredFadeIn index={listIndex}>
            <View style={challengesPageStyles.sectionContainer}>
              <ArenaSectionHeader
                sectionBucket="finished"
                title={SECTION_TITLES_FINISHED}
                count={item.challenges.length}
              />
              {item.challenges.map((challenge) => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  currentUserId={currentUserId}
                  onPress={() => handleChallengePress(challenge)}
                  onShowMessage={handleShowMessage}
                  variant="home"
                  sectionBucket="finished"
                />
              ))}
              <Pressable
                onPress={() => setActiveFilter('finished')}
                testID="history-preview-see-all"
                style={({ pressed, hovered }) => [
                  challengesPageStyles.historyPreviewSeeAllRow,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && challengesPageStyles.historyPreviewSeeAllRowPressed,
                  hovered && Platform.OS === 'web' && challengesPageStyles.historyPreviewSeeAllRowHover,
                ]}
              >
                <Text style={challengesPageStyles.historyPreviewSeeAllText}>
                  View all history
                </Text>
                <ChevronRight size={15} color={theme.primary} />
              </Pressable>
            </View>
          </StaggeredFadeIn>
        );
      }

      if (item.type === 'finishedSkeleton') {
        return (
          <StaggeredFadeIn index={listIndex}>
            <View style={challengesPageStyles.sectionContainer}>
              <ArenaSectionHeader
                sectionBucket="finished"
                title={SECTION_TITLES_FINISHED}
              />
              {[1, 2, 3].map((i) => (
                <ChallengeCardSkeleton key={`finished-skeleton-${i}`} />
              ))}
            </View>
          </StaggeredFadeIn>
        );
      }

      if (item.type === 'section') {
        return (
          <StaggeredFadeIn index={listIndex}>
            <View
              ref={listIndex === 0 ? liveCardRef : undefined}
              style={challengesPageStyles.sectionContainer}
            >
              <ArenaSectionHeader
                sectionBucket={item.sectionBucket}
                title={item.title}
                count={item.challenges.length}
              />
              {item.challenges.map((challenge) => (
                <ChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  currentUserId={currentUserId}
                  onPress={() => handleChallengePress(challenge)}
                  onShowMessage={handleShowMessage}
                  variant="home"
                  sectionBucket={item.sectionBucket}
                />
              ))}
            </View>
          </StaggeredFadeIn>
        );
      }
      return null;
    },
    [
      challengesPageStyles,
      currentUserId,
      handleChallengePress,
      handleShowMessage,
      setActiveFilter,
      theme.primary,
      liveCardRef,
    ]
  );

  const keyExtractor = useCallback((item: ArenaListItem, index: number) => {
    if (item.type === 'section') return `section-${item.sectionBucket}-${index}`;
    if (item.type === 'historyPreview') return 'history-preview';
    if (item.type === 'finishedSkeleton') return 'finished-skeleton';
    return 'loadMore';
  }, []);

  const ListFooterComponent = useMemo(() => {
    if (isLoadingWithDelay) return null;
    if (!isFetchingNextPage) return null;
    return (
      <View style={challengesPageStyles.loadMoreContainer}>
        {[1, 2, 3].map((i) => (
          <ChallengeCardSkeleton key={`footer-skeleton-${i}`} />
        ))}
      </View>
    );
  }, [isLoadingWithDelay, isFetchingNextPage, challengesPageStyles.loadMoreContainer]);

  const handleRetryActiveChallenges = useCallback(() => {
    void refetchActive();
  }, [refetchActive]);

  const handleRetryArenaStats = useCallback(() => {
    void refetchProfile();
  }, [refetchProfile]);

  const ListHeaderComponent = useMemo(
    () => (
      <>
        {/* Guest sign-up banner: inside the list header so it clears the web-fixed header. */}
        <GuestBanner />
        {isLoadingWithDelay ? (
          <ArenaLoadingSkeleton />
        ) : (
          <>
            <ArenaStatsBanner
              stats={arenaStats}
              isLoading={arenaStatsLoading}
              error={arenaStatsError}
              onRetry={handleRetryArenaStats}
            />

          <View style={challengesPageStyles.filterBarRow}>
            {FILTER_OPTIONS.map((opt) => {
              const isActive = activeFilter === opt.key;
              return (
                <Pressable
                  key={opt.key}
                  style={({ pressed, hovered }) => [
                    challengesPageStyles.filterButton,
                    isActive && challengesPageStyles.filterButtonActive,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && challengesPageStyles.filterButtonPressed,
                    hovered && Platform.OS === 'web' && challengesPageStyles.filterButtonHover,
                  ]}
                  onPress={() => setActiveFilter(opt.key)}
                >
                  <Text
                    style={[
                      challengesPageStyles.filterButtonText,
                      isActive && challengesPageStyles.filterButtonTextActive,
                    ]}
                  >
                    {opt.label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          </>
        )}
      </>
    ),
    [
      challengesPageStyles,
      activeFilter,
      setActiveFilter,
      isLoadingWithDelay,
      arenaStats,
      arenaStatsLoading,
      arenaStatsError,
      handleRetryArenaStats,
    ]
  );

  const showChallengesLoadError =
    isActiveError &&
    isInitialLoadComplete &&
    !isLoadingWithDelay &&
    challengesList.length === 0 &&
    previewList.length === 0 &&
    historyList.length === 0;

  const ListEmptyComponent = useMemo(() => {
    if (isLoadingWithDelay) return null;
    if (showChallengesLoadError) {
      return (
        <DataLoadErrorPanel
          title={ARENA_CHALLENGES_ERROR_COPY.TITLE}
          message={ARENA_CHALLENGES_ERROR_COPY.MESSAGE}
          onRetry={handleRetryActiveChallenges}
          testID="arena-challenges-error"
        />
      );
    }
    const arenaEmptyCopy = isGuest
      ? ARENA_EMPTY_STATE_COPY.GUEST
      : ARENA_EMPTY_STATE_COPY.SIGNED_IN;
    return (
      <View
        style={
          challengesList.length === 0 && historyList.length === 0
            ? challengesPageStyles.emptyContainer
            : challengesPageStyles.emptyFilteredWrapper
        }
      >
        {challengesList.length === 0 && historyList.length === 0 ? (
          <>
            <ArenaEmptyStateMonkey />
            <Text style={challengesPageStyles.emptyArenaTitle}>{arenaEmptyCopy.TITLE}</Text>
            <Text style={challengesPageStyles.emptyArenaSubtitle}>{arenaEmptyCopy.SUBTITLE}</Text>
            <Pressable
              style={({ pressed, hovered }) => [
                challengesPageStyles.emptyActionButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && challengesPageStyles.emptyActionButtonPressed,
                hovered && Platform.OS === 'web' && challengesPageStyles.emptyActionButtonHover,
              ]}
              onPress={() => {
                if (isGuest) {
                  requireAuth('create challenges and compete with friends');
                } else {
                  router.push('/app/create');
                }
              }}
            >
              <Text style={challengesPageStyles.emptyActionButtonText}>{arenaEmptyCopy.CTA}</Text>
            </Pressable>
          </>
        ) : (
          <>
            <View style={challengesPageStyles.emptyFilteredIconWrap}>
              <ListFilter
                size={40}
                color={theme.primary}
                style={challengesPageStyles.emptyFilteredIcon}
              />
            </View>
            <Text style={challengesPageStyles.emptyFilteredTitle}>
              No {FILTER_OPTIONS.find((o) => o.key === activeFilter)?.label ?? activeFilter} challenges
            </Text>
            <Text style={challengesPageStyles.emptyFilteredSubtitle}>
              {EMPTY_FILTER_SUBTITLE}
            </Text>
          </>
        )}
      </View>
    );
  }, [
    isLoadingWithDelay,
    showChallengesLoadError,
    handleRetryActiveChallenges,
    challengesList.length,
    historyList.length,
    activeFilter,
    challengesPageStyles,
    theme.primary,
    isGuest,
    requireAuth,
  ]);

  const hasNonFinishedSections = useMemo(
    () =>
      listData.some(
        (item) =>
          item.type === 'section' &&
          'sectionBucket' in item &&
          item.sectionBucket !== 'finished'
      ),
    [listData]
  );

  const contentContainerStyle = useMemo(() => {
    if (isEmptyState) return challengesPageStyles.challengesListContentEmpty;
    if (isFilteredEmpty) return challengesPageStyles.challengesListContentFilteredEmpty;
    if (listData.length > 0 && hasNonFinishedSections) {
      return (challengesPageStyles as { challengesListContentWithItems?: object }).challengesListContentWithItems;
    }
    return undefined;
  }, [
    isEmptyState,
    isFilteredEmpty,
    listData.length,
    hasNonFinishedSections,
    challengesPageStyles,
  ]);

  const listWrapperStyle = (challengesPageStyles as { challengesListWrapper?: object }).challengesListWrapper ?? { flex: 1 };

  return (
    <>
      <FadeInContent opacity={contentOpacity} style={listWrapperStyle}>
        <FlashList
          ref={listRef}
          overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
          data={isLoadingWithDelay ? [] : listData}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          ListHeaderComponent={ListHeaderComponent}
          ListFooterComponent={ListFooterComponent}
          ListEmptyComponent={ListEmptyComponent}
          contentContainerStyle={contentContainerStyle}
          style={challengesPageStyles.challengesList}
          testID="challenges-list"
          showsVerticalScrollIndicator={false}
          drawDistance={500}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          onScroll={handleScroll}
        />
      </FadeInContent>

      <GameResultModal
        isVisible={resultModalVisible}
        onDismiss={() => {
          markAllSeen();
          setResultModalVisible(false);
        }}
        results={pendingResults}
        hasOverflow={hasOverflow}
      />
    </>
  );
};

